/**
 * How long a changed setting waits before it is applied:
 * - `instant`: cheap, purely visual toggles and selects.
 * - `short`: values edited while typing or dragging, where every keystroke would otherwise rebuild shaders.
 * - `delayed`: settings that reload data, recreate workers or rebuild the scene; shown with a visible countdown.
 */
export type SettingsApplyTier = 'instant' | 'short' | 'delayed';

export const SETTINGS_APPLY_DELAYS_MS: Readonly<Record<SettingsApplyTier, number>> = {
  instant: 0,
  short: 300,
  delayed: 10_000,
};

const COUNTDOWN_TICK_MS: number = 250;
const BUSY_RETRY_MS: number = 1000;

export interface SettingsAutoApplySchedulerOptions {
  apply: () => Promise<void> | void;
  /** True while a scene load or frustum refresh is running; the apply is postponed until it is false. */
  isBlocked: () => boolean;
  /** Called whenever the countdown or the pending state changes, so the view can refresh. */
  onChange: () => void;
  delaysMs?: Readonly<Record<SettingsApplyTier, number>>;
}

/**
 * Debounces applying of edited settings. Every change restarts the countdown, and the countdown
 * length is that of the slowest tier among the changes made since the last apply.
 */
export class SettingsAutoApplyScheduler {
  private readonly delaysMs: Readonly<Record<SettingsApplyTier, number>>;
  private pendingDelayMs: number = 0;
  private deadline: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  private applying: boolean = false;

  public constructor(private readonly options: SettingsAutoApplySchedulerOptions) {
    this.delaysMs = options.delaysMs ?? SETTINGS_APPLY_DELAYS_MS;
  }

  public get pending(): boolean {
    return this.deadline !== null || this.applying;
  }

  /** Whole seconds left when a long countdown is running, otherwise null. */
  public get secondsLeft(): number | null {
    if (this.deadline === null || this.pendingDelayMs < this.delaysMs.delayed) {
      return null;
    }
    return Math.max(1, Math.ceil((this.deadline - Date.now()) / 1000));
  }

  public schedule(tier: SettingsApplyTier): void {
    this.arm(Math.max(this.deadline === null ? 0 : this.pendingDelayMs, this.delaysMs[tier]));
  }

  /** Applies immediately when something is pending. Safe to call repeatedly. */
  public flush(): void {
    if (this.deadline === null) {
      return;
    }
    this.clearTimers();
    void this.run();
  }

  public cancel(): void {
    const wasPending: boolean = this.deadline !== null;
    this.clearTimers();
    if (wasPending) {
      this.options.onChange();
    }
  }

  public dispose(): void {
    this.clearTimers();
  }

  private arm(delayMs: number): void {
    this.clearTimers();
    this.pendingDelayMs = delayMs;
    this.deadline = Date.now() + delayMs;
    if (delayMs <= 0) {
      void this.run();
      return;
    }
    this.timer = setTimeout(() => {
      this.clearTimers();
      void this.run();
    }, delayMs);
    if (delayMs >= this.delaysMs.delayed) {
      this.ticker = setInterval(() => this.options.onChange(), COUNTDOWN_TICK_MS);
    }
    this.options.onChange();
  }

  private async run(): Promise<void> {
    if (this.applying || this.options.isBlocked()) {
      this.deadline = Date.now() + BUSY_RETRY_MS;
      this.pendingDelayMs = 0;
      this.timer = setTimeout(() => {
        this.clearTimers();
        void this.run();
      }, BUSY_RETRY_MS);
      this.options.onChange();
      return;
    }
    this.applying = true;
    this.deadline = null;
    this.pendingDelayMs = 0;
    try {
      await this.options.apply();
    } finally {
      this.applying = false;
      this.options.onChange();
    }
  }

  private clearTimers(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.ticker !== null) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
    this.deadline = null;
    this.pendingDelayMs = 0;
  }
}
